import React from "react";
import { createPortal } from "react-dom";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { Save, CheckCircle2, TriangleAlert, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../components/feedback/ToastProvider";
import "./HomebuyerDossierLayout.css";
import "./HomebuyerDossierMissingFieldsModal.css";
import "./HomebuyerDossierUnsavedModal.css";
import "../dossier/steps/step-theme.css";
import { useAuth0 } from "@auth0/auth0-react";
import { mapDossierDetailToDraft } from "../dossier/steps/mapDossierDetailtoDraft";
import {
  validateDossierStepCompletion,
  dossierEditableFingerprint,
} from "../dossier/steps/dossierCompletionValidation";
import {
  prepareDossierDownload,
  fetchParticipatingLenders,
  downloadDossierZip,
} from "../dossier/steps/homebuyerDossierApi";

const DOSSIER_STORAGE_KEY = "legup_hb_dossier_draft";
const DOSSIER_META_STORAGE_KEY = "legup_hb_dossier_meta";

const DOSSIER_STEP_CODE_BY_KEY = Object.freeze({
  "personal-info": "PERSONAL",
  "financial-info": "FINANCIAL",
  documents: "DOCUMENTS",
  "extracted-data": "EXTRACTION",
  "property-information": "PROPERTY",
  "review-download": "REVIEW",
});

const DOSSIER_STEP_KEY_BY_CODE = Object.freeze({
  ...Object.fromEntries(
    Object.entries(DOSSIER_STEP_CODE_BY_KEY).map(([key, code]) => [code, key]),
  ),
  // The extracted-data screen is disabled in the current five-step wizard.
  EXTRACTION: "documents",
});

function buildScopedStorageKey(baseKey, ownerId) {
  if (!ownerId) {
    return null;
  }

  return `${baseKey}:${encodeURIComponent(ownerId)}`;
}

const STEP_SECTION_MAP = {
  "property-information": "property_information",
  "review-download": "review_download",
};

const EMPTY_PERSONAL_INFO = {
  first_name: "",
  last_name: "",
  date_of_birth: "",
  nationality: "",
  country_of_residence: "",
  residency_status: "",
  national_id_number: "",
  address: "",
  email: "",
  phone: "",
  marital_status: "",
  dependents: "",
  disability: 0,
  preferred_contact_method: "EMAIL",
  notes: "",
};

const EMPTY_FINANCIAL_INFO = {
  net_income: "",
  listing_in_cpk: "",
  monthly_expenses: "",
  current_rent: "",
  employment_status: "",
  contract_type: "",
  employment_contracts_count: "",
  politically_exposed_person: "",
  notes: "",
};

function createBorrower(role = "PRIMARY") {
  return {
    id: role === "PRIMARY" ? "primary" : "co_applicant",
    role,
    personal_info: { ...EMPTY_PERSONAL_INFO },
    financial_info: { ...EMPTY_FINANCIAL_INFO },
    documents: {},
    extracted_data: {},
  };
}

const DEFAULT_DOSSIER_DRAFT = {
  dossier_id: null,
  dossier_applicant_id: null,
  primary_dossier_applicant_id: null,
  co_dossier_applicant_id: null,
  dossier_code: null,
  country_of_property_purchase: "",
  market_region_id: null,
  market_region: null,
  document_requirements: [],
  document_requirement_map_by_code: {},
  requires_reextraction: false,
  completed_steps: {},
  borrowers: [createBorrower("PRIMARY")],

  dossier_financial_info: {
    reuse_simulation_file_name: "",
    total_loan_amount: "",
    renovation_loan_amount: "",
    equity_amount: "",
    property_price: "",
    tenure_years: 9,
    monthly_cost_percent: 40,
    estimated_monthly_payment: "",
    notary_of_choice: "",
    repayment_bank_account: "",
    loan_disbursement_bank_account: "",
    notes: "",
  },

  property_information: {
    property_id: null,
    prefilled_from_existing_property: false,
    property_type: "",
    building_condition: "",
    surface_sqm: "",
    number_of_rooms: "",
    year_of_build: "",
    has_lift: "",
    comfort_type: "",
    notes: "",
    street_name: "",
    house_number: "",
    city: "",
    postal_code: "",
    country: "",
    apartment_number: "",
    full_address: "",
    latitude: null,
    longitude: null,
    place_id: "",
    epc_rating_current: "",
    epc_rating_target: "",
    kwh_sqm_year_current: "",
    kwh_sqm_year_target: "",
    heating_type: "",
    cooling_type: "",
    land_register_id: "",
    ownership_type: "",
    photos: [],
  },

  review_download: {},
  financial_indicators: null,
};

function normalizeStoredDraft(parsed = {}) {
  return {
    ...DEFAULT_DOSSIER_DRAFT,
    ...parsed,

    borrowers:
      Array.isArray(parsed.borrowers) && parsed.borrowers.length > 0
        ? parsed.borrowers
        : DEFAULT_DOSSIER_DRAFT.borrowers,

    dossier_financial_info: {
      ...DEFAULT_DOSSIER_DRAFT.dossier_financial_info,
      ...(parsed.dossier_financial_info || {}),
    },

    property_information: {
      ...DEFAULT_DOSSIER_DRAFT.property_information,
      ...(parsed.property_information || {}),
    },

    review_download: {
      ...DEFAULT_DOSSIER_DRAFT.review_download,
      ...(parsed.review_download || {}),
    },

    document_requirements: parsed.document_requirements || [],

    document_requirement_map_by_code:
      parsed.document_requirement_map_by_code || {},
  };
}
function isFilled(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function hasMeaningfulValues(obj = {}) {
  return Object.values(obj).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return hasMeaningfulValues(value);
    return isFilled(value);
  });
}

function getPrimaryBorrower(draft) {
  const borrowers = draft?.borrowers || [];
  return (
    borrowers.find((b) => b?.role === "PRIMARY") ||
    borrowers[0] ||
    createBorrower("PRIMARY")
  );
}

function getCoBorrower(draft) {
  const borrowers = draft?.borrowers || [];
  return borrowers.find((b) => b?.role === "CO_APPLICANT") || null;
}

function shouldHaveCoBorrowerFromBorrowers(borrowers = []) {
  const primary = borrowers.find((b) => b?.role === "PRIMARY") || borrowers[0];
  const maritalStatus = primary?.personal_info?.marital_status || "";

  return (
    maritalStatus && !["SINGLE", "DIVORCED", "WIDOWED"].includes(maritalStatus)
  );
}

function normalizeBorrowersByMaritalStatus(borrowers = []) {
  const primary = borrowers.find((b) => b?.role === "PRIMARY") || borrowers[0];

  if (!primary) return [createBorrower("PRIMARY")];

  const shouldHaveCoBorrower = shouldHaveCoBorrowerFromBorrowers(borrowers);

  if (!shouldHaveCoBorrower) {
    return [primary];
  }

  const co =
    borrowers.find((b) => b?.role === "CO_APPLICANT") ||
    createBorrower("CO_APPLICANT");

  return [primary, co];
}

function validateBorrowerPersonalInfo(
  personal,
  stepLabel,
  addMissing,
  { mode = "primary" } = {},
) {
  if (!isFilled(personal.first_name)) addMissing(stepLabel, "First name");
  if (!isFilled(personal.last_name)) addMissing(stepLabel, "Last name");
  if (!isFilled(personal.date_of_birth)) addMissing(stepLabel, "Date of birth");
  if (!isFilled(personal.email)) addMissing(stepLabel, "Email");

  if (mode === "primary") {
    if (!isFilled(personal.nationality)) addMissing(stepLabel, "Nationality");
    if (!isFilled(personal.country_of_residence)) {
      addMissing(stepLabel, "Country of residence");
    }
    if (!isFilled(personal.residency_status)) {
      addMissing(stepLabel, "Residency status");
    }

    // National ID is optional.

    if (!isFilled(personal.address)) addMissing(stepLabel, "Address");
    if (!isFilled(personal.phone)) addMissing(stepLabel, "Phone number");
    if (!isFilled(personal.marital_status)) {
      addMissing(stepLabel, "Marital status");
    }
    if (!isFilled(personal.dependents)) {
      addMissing(stepLabel, "Dependents");
    }
    if (!isFilled(personal.disability) && personal.disability !== 0) {
      addMissing(stepLabel, "Disability");
    }
    if (!isFilled(personal.preferred_contact_method)) {
      addMissing(stepLabel, "Preferred contact method");
    }
  }
}

function validateBorrowerFinancialInfo(financial, stepLabel, addMissing) {
  if (!isFilled(financial.net_income)) addMissing(stepLabel, "Net income");
  if (!isFilled(financial.listing_in_cpk)) {
    addMissing(stepLabel, "Credit register status");
  }
  if (!isFilled(financial.monthly_expenses)) {
    addMissing(stepLabel, "Monthly expenses");
  }
  if (!isFilled(financial.employment_status)) {
    addMissing(stepLabel, "Employment status");
  }
  if (!isFilled(financial.contract_type)) {
    addMissing(stepLabel, "Contract type");
  }
  if (!isFilled(financial.employment_contracts_count)) {
    addMissing(stepLabel, "Employment contracts count");
  }
  if (!isFilled(financial.politically_exposed_person)) {
    addMissing(stepLabel, "PEP status");
  }
}

function validateDossierForDownload(draft) {
  const check = validateDossierStepCompletion(draft);
  const labels = {
    "personal-info": "Personal Info",
    "financial-info": "Financial Info",
    documents: "Documents",
    "property-information": "Property Information",
  };
  const missingGroups = Object.entries(labels)
    .filter(([key]) => !check[key]?.isValid)
    .map(([key, step]) => ({ key, step, fields: check[key]?.errors || [] }));
  return { isValid: missingGroups.length === 0, missingGroups };
}

// Convert the shared validator's technical issues into user-facing, localized
// labels. This is display-only; review eligibility still uses the shared validator.
const MISSING_MODAL_STEP_LABEL_KEYS = {
  "personal-info": "steps.personalInfo",
  "financial-info": "steps.financialInfo",
  documents: "steps.documents",
  "property-information": "steps.propertyInformation",
};

const MISSING_MODAL_SPECIAL_ISSUES = {
  "Market region": "market_region",
  "Country of purchase": "country_of_purchase",
  "Residency status (other)": "residency_status_other",
  "Target EPC": "epc_rating_target",
  "Cadastral reference": "land_register_id",
};

const MISSING_MODAL_INVALID_FIELDS = {
  "Invalid date of birth": "date_of_birth",
  "Invalid email address": "email",
  "Invalid phone number": "phone",
  "Invalid dependents": "dependents",
  "Invalid employment contracts count": "employment_contracts_count",
  "Invalid equity amount": "equity_amount",
  "Invalid property price": "property_price",
  "Invalid tenure": "tenure_years",
  "Invalid monthly cost percentage": "monthly_cost_percent",
  "Invalid surface": "surface_sqm",
  "Invalid building year": "year_of_build",
  "Invalid current kWh/m²/year": "kwh_sqm_year_current",
  "Invalid target kWh/m²/year": "kwh_sqm_year_target",
  "Invalid current EPC": "epc_rating_current",
  "Invalid target EPC": "epc_rating_target",
};

const MISSING_MODAL_SPECIAL_MESSAGES = {
  "Equity exceeds property price": "equityExceedsPrice",
  "Target kWh must be lower": "targetEnergyLower",
  "Target EPC must improve": "targetEpcImprove",
  "Address not verified": "addressNotVerified",
  "Document requirements not loaded": "documentsNotLoaded",
  "Review confirmation and terms": "reviewConfirmation",
};

function missingModalFieldLabel(key, t) {
  const fallback = String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
  return t(`missingModal.fields.${key}`, { defaultValue: fallback });
}

function resolveMissingModalIssue(rawIssue, groupKey, requirements, t) {
  const raw = String(rawIssue || "").trim();

  if (groupKey === "documents") {
    const matchingRequirement = requirements.find(
      (req) =>
        raw === String(req.document_type_label || "") ||
        raw === String(req.document_type_code || "") ||
        raw === `Document ${req.id}`,
    );
    if (matchingRequirement) {
      const code = matchingRequirement.document_type_code;
      return {
        text: t(`documentLabels.${code}`, {
          ns: "homebuyer-documents",
          defaultValue: matchingRequirement.document_type_label || code || raw,
        }),
      };
    }
  }

  const specialMessage = MISSING_MODAL_SPECIAL_MESSAGES[raw];
  if (specialMessage) {
    return {
      text: t(`missingModal.messages.${specialMessage}`, { defaultValue: raw }),
    };
  }

  const ibanMatch = raw.match(
    /^Invalid (repayment_bank_account|loan_disbursement_bank_account) \(IBAN\)$/,
  );
  if (ibanMatch) {
    return {
      fieldKey: ibanMatch[1],
      invalid: true,
      text: t("missingModal.messages.invalidIban", {
        field: missingModalFieldLabel(ibanMatch[1], t),
        defaultValue: "Invalid IBAN: {{field}}",
      }),
    };
  }

  const invalidKey =
    MISSING_MODAL_INVALID_FIELDS[raw] ||
    (raw.startsWith("Invalid ") && /^Invalid [a-z][a-z_]*$/.test(raw)
      ? raw.slice(8)
      : null);
  if (invalidKey) {
    return {
      fieldKey: invalidKey,
      invalid: true,
      text: t("missingModal.messages.invalidField", {
        field: missingModalFieldLabel(invalidKey, t),
        defaultValue: "Invalid value: {{field}}",
      }),
    };
  }

  const key = MISSING_MODAL_SPECIAL_ISSUES[raw] || raw;
  if (/^[a-z][a-z0-9_]*$/.test(key)) {
    return {
      fieldKey: key,
      invalid: false,
      text: missingModalFieldLabel(key, t),
    };
  }

  // Unknown backend validators still show a readable fallback, never a raw code.
  return { text: raw.replace(/_/g, " ") };
}

function isPersonalInfoStepComplete(personal, { mode = "primary" } = {}) {
  const baseComplete =
    isFilled(personal.first_name) &&
    isFilled(personal.last_name) &&
    isFilled(personal.date_of_birth) &&
    isFilled(personal.email);

  if (mode === "coBorrower") {
    return baseComplete;
  }

  return (
    baseComplete &&
    isFilled(personal.nationality) &&
    isFilled(personal.country_of_residence) &&
    isFilled(personal.residency_status) &&
    isFilled(personal.address) &&
    isFilled(personal.phone) &&
    isFilled(personal.marital_status) &&
    isFilled(personal.dependents) &&
    (isFilled(personal.disability) || personal.disability === 0) &&
    isFilled(personal.preferred_contact_method)
  );
}

function isFinancialInfoStepComplete(draft) {
  const primaryBorrower = getPrimaryBorrower(draft);
  const coBorrower = getCoBorrower(draft);

  const primaryFinancial = primaryBorrower?.financial_info || {};
  const coFinancial = coBorrower?.financial_info || {};
  const sharedFinancial = draft?.dossier_financial_info || {};

  const primaryComplete =
    isFilled(primaryFinancial.net_income) &&
    isFilled(primaryFinancial.listing_in_cpk) &&
    isFilled(primaryFinancial.monthly_expenses) &&
    isFilled(primaryFinancial.employment_status) &&
    isFilled(primaryFinancial.contract_type) &&
    isFilled(primaryFinancial.employment_contracts_count) &&
    isFilled(primaryFinancial.politically_exposed_person);

  const coComplete = !coBorrower
    ? true
    : isFilled(coFinancial.net_income) &&
      isFilled(coFinancial.listing_in_cpk) &&
      isFilled(coFinancial.monthly_expenses) &&
      isFilled(coFinancial.employment_status) &&
      isFilled(coFinancial.contract_type) &&
      isFilled(coFinancial.employment_contracts_count) &&
      isFilled(coFinancial.politically_exposed_person);

  const sharedComplete =
    isFilled(sharedFinancial.equity_amount) &&
    isFilled(sharedFinancial.property_price) &&
    isFilled(sharedFinancial.tenure_years) &&
    isFilled(sharedFinancial.repayment_bank_account) &&
    isFilled(sharedFinancial.loan_disbursement_bank_account);

  return primaryComplete && coComplete && sharedComplete;
}

function isDocumentsStepComplete(draft) {
  if (!draft?.dossier_id) {
    return false;
  }

  const requirements = draft?.document_requirements || [];

  if (!Array.isArray(requirements) || requirements.length === 0) {
    return false;
  }

  const requiredRequirements = requirements.filter(
    (requirement) => requirement?.is_required === true,
  );

  if (requiredRequirements.length === 0) {
    return true;
  }

  return requiredRequirements.every((requirement) => {
    const files = requirement?.files || requirement?.document_files || [];

    const activeFiles = files.filter((file) => {
      if (file?.is_active === false) {
        return false;
      }

      if (file?.latest_version?.is_active) {
        return true;
      }

      return (file?.versions || []).some((version) => version?.is_active);
    });

    const minimumRequired = Math.max(Number(requirement?.min_files || 1), 1);

    return activeFiles.length >= minimumRequired;
  });
}
function isExtractedDataStepComplete(draft) {
  const primaryBorrower = getPrimaryBorrower(draft);
  const coBorrower = getCoBorrower(draft);

  return (
    hasMeaningfulValues(primaryBorrower?.extracted_data || {}) &&
    (!coBorrower || hasMeaningfulValues(coBorrower?.extracted_data || {}))
  );
}

function normalizeCountryCode(value) {
  const text = String(value || "")
    .trim()
    .toUpperCase();

  if (["ES", "SPAIN", "ESPAÑA", "ESPANA"].includes(text)) return "ES";
  if (["BE", "BELGIUM", "BELGIQUE"].includes(text)) return "BE";

  return text;
}

function isPropertyInfoStepComplete(draft) {
  const property = draft?.property_information || {};
  const country = normalizeCountryCode(property.country);

  return (
    isFilled(property.property_type) &&
    isFilled(property.building_condition) &&
    isFilled(property.surface_sqm) &&
    isFilled(property.year_of_build) &&
    isFilled(property.epc_rating_current) &&
    (country !== "BE" || isFilled(property.epc_rating_target)) &&
    isFilled(property.kwh_sqm_year_current) &&
    isFilled(property.kwh_sqm_year_target) &&
    isFilled(property.heating_type) &&
    isFilled(property.cooling_type) &&
    isFilled(property.ownership_type) &&
    isFilled(property.street_name) &&
    isFilled(property.house_number) &&
    isFilled(property.city) &&
    isFilled(property.postal_code) &&
    isFilled(property.country) &&
    (country !== "ES" || isFilled(property.land_register_id))
  );
}

function isNonEmpty(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function mergePreservingExisting(existing = {}, incoming = {}) {
  const merged = { ...existing };

  Object.entries(incoming || {}).forEach(([key, value]) => {
    if (isNonEmpty(value) || value === 0 || value === false) {
      merged[key] = value;
    }
  });

  return merged;
}

function mergeBorrowers(existingBorrowers = [], incomingBorrowers = []) {
  const result = [...existingBorrowers];

  incomingBorrowers.forEach((incomingBorrower) => {
    const index = result.findIndex((b) => b.role === incomingBorrower.role);

    if (index === -1) {
      result.push(incomingBorrower);
      return;
    }

    const existingBorrower = result[index];

    result[index] = {
      ...existingBorrower,
      id: incomingBorrower.id || existingBorrower.id,
      role: incomingBorrower.role || existingBorrower.role,

      personal_info: mergePreservingExisting(
        existingBorrower.personal_info,
        incomingBorrower.personal_info,
      ),

      financial_info: mergePreservingExisting(
        existingBorrower.financial_info,
        incomingBorrower.financial_info,
      ),

      documents: {
        ...(existingBorrower.documents || {}),
        ...(incomingBorrower.documents || {}),
      },

      extracted_data: {
        ...(existingBorrower.extracted_data || {}),
        ...(incomingBorrower.extracted_data || {}),
      },
    };
  });

  return result;
}

export default function HomebuyerDossierLayout() {
  const parentContext = useOutletContext();
  const refreshMe = parentContext?.refreshMe;
  const account = parentContext?.account;
  const profile = parentContext?.profile;
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation([
    "homebuyer-dossier-layout",
    "homebuyer-documents",
  ]);
  const { showToast } = useToast();

  const storageOwnerId = auth0User?.sub || account?.email || "";

  const dossierStorageKey = React.useMemo(
    () => buildScopedStorageKey(DOSSIER_STORAGE_KEY, storageOwnerId),
    [storageOwnerId],
  );

  const dossierMetaStorageKey = React.useMemo(
    () => buildScopedStorageKey(DOSSIER_META_STORAGE_KEY, storageOwnerId),
    [storageOwnerId],
  );

  const persistScopedDraft = React.useCallback(
    (draft) => {
      if (!dossierStorageKey) {
        return;
      }

      localStorage.setItem(dossierStorageKey, JSON.stringify(draft));
    },
    [dossierStorageKey],
  );

  const [participatingLenders, setParticipatingLenders] = React.useState([]);
  const [isLoadingLenders, setIsLoadingLenders] = React.useState(false);

  const [showLenders, setShowLenders] = React.useState(false);

  React.useEffect(() => {
    if (location.state?.downloadSuccess) {
      refreshMe?.();
    }
  }, [location.state, refreshMe]);

  const STEPS = React.useMemo(
    () => [
      {
        number: 1,
        key: "personal-info",
        label: t("steps.personalInfo"),
        path: "/hb/dossier/form/personal-info",
      },
      {
        number: 2,
        key: "financial-info",
        label: t("steps.financialInfo"),
        path: "/hb/dossier/form/financial-info",
      },
      {
        number: 3,
        key: "documents",
        label: t("steps.documents"),
        path: "/hb/dossier/form/documents",
      },

      // Extracted Data will be added later.
      // {
      //   number: 4,
      //   key: "extracted-data",
      //   label: t("steps.extractedData"),
      //   path: "/hb/dossier/form/extracted-data",
      // },

      {
        number: 4,
        key: "property-information",
        label: t("steps.propertyInformation"),
        path: "/hb/dossier/form/property-information",
      },
      {
        number: 5,
        key: "review-download",
        label: t("steps.reviewDownload", "Review & Download"),
        path: "/hb/dossier/form/review-download",
      },
    ],
    [t],
  );

  const [dossierDraft, setDossierDraft] = React.useState(DEFAULT_DOSSIER_DRAFT);
  // Prevent a child step from publishing its initial empty form over the server
  // response while the authenticated dossier is still loading.
  const [draftLoadState, setDraftLoadState] = React.useState({
    ready: false,
    error: null,
  });
  const [draftReloadNonce, setDraftReloadNonce] = React.useState(0);

  const lastSavedFingerprintRef = React.useRef(null);
  const latestDraftFingerprintRef = React.useRef(null);
  const suppressLeavePromptRef = React.useRef(false);
  const hasUnsavedChanges = React.useCallback(
    () =>
      draftLoadState.ready &&
      lastSavedFingerprintRef.current !== null &&
      latestDraftFingerprintRef.current !== null &&
      lastSavedFingerprintRef.current !== latestDraftFingerprintRef.current,
    [draftLoadState.ready],
  );

  React.useEffect(() => {
    latestDraftFingerprintRef.current =
      dossierEditableFingerprint(dossierDraft);
  }, [dossierDraft]);

  // Intercept *app navigation clicks* in the outer Homebuyer shell. With the
  // existing BrowserRouter, there is no reliable router-wide useBlocker here.
  // We therefore pause these clicks and replay the original click only after
  // the user confirms. This preserves the shell's onClick/onNavigateRequest.
  //
  // Browser refresh, tab close and address-bar navigation cannot show a React
  // modal. We intentionally do NOT install beforeunload, because the user
  // explicitly does not want the browser-native confirmation dialogue.
  const [pendingLeave, setPendingLeave] = React.useState(null);
  const pendingLeaveRef = React.useRef(null);
  const keepEditingRef = React.useRef(null);

  const closeLeaveModal = React.useCallback(() => {
    pendingLeaveRef.current = null;
    setPendingLeave(null);
  }, []);

  const confirmLeaveDossier = React.useCallback(() => {
    const pending = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setPendingLeave(null);
    if (!pending) return;

    // Let the original click proceed, so sidebar callbacks, navigation guards,
    // mobile menu state and React Router Links keep their normal behaviour.
    suppressLeavePromptRef.current = true;
    try {
      if (
        pending.element?.isConnected &&
        typeof pending.element.click === "function"
      ) {
        pending.element.click();
      } else if (pending.href) {
        const destination = new URL(pending.href, window.location.href);
        if (destination.origin === window.location.origin) {
          navigate(
            `${destination.pathname}${destination.search}${destination.hash}`,
          );
        } else {
          window.location.assign(destination.href);
        }
      }
    } finally {
      // If a sidebar callback did not navigate, re-arm the guard for later.
      window.setTimeout(() => {
        suppressLeavePromptRef.current = false;
      }, 0);
    }
  }, [navigate]);

  React.useEffect(() => {
    const onOutsideNavigationClick = (event) => {
      if (
        suppressLeavePromptRef.current ||
        !hasUnsavedChanges() ||
        pendingLeaveRef.current ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.shiftKey
      )
        return;

      const element = event.target instanceof Element ? event.target : null;
      const clickable = element?.closest(
        "a[href], button, [role='link'], [role='button']",
      );
      if (
        !clickable ||
        clickable.closest(".hb-app-flow, .hb-dossier-unsaved-backdrop")
      )
        return;

      const href = clickable.getAttribute("href");
      const isAnchor = clickable.matches("a[href]");
      if (isAnchor) {
        // Don't interfere with in-page anchors, downloads, mailto or new tabs.
        if (
          !href ||
          href.startsWith("#") ||
          clickable.hasAttribute("download") ||
          (clickable.getAttribute("target") &&
            clickable.getAttribute("target") !== "_self")
        )
          return;
        let target;
        try {
          target = new URL(href, window.location.href);
        } catch {
          return;
        }
        if (!["http:", "https:"].includes(target.protocol)) return;
        if (target.href === window.location.href) return;
      } else {
        const inNavigation = clickable.closest(
          "nav, aside, [class*='sidebar']",
        );
        const isUtilityControl = clickable.closest(
          "[class*='lang'], [class*='notification'], [class*='profile'], [class*='popover'], [class*='theme']",
        );
        if (!inNavigation || isUtilityControl) return;
      }

      // Capture phase: stop the app sidebar's React onClick until the modal is answered.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      const attempt = { element: clickable, href: href || null };
      pendingLeaveRef.current = attempt;
      setPendingLeave(attempt);
    };

    document.addEventListener("click", onOutsideNavigationClick, true);
    return () =>
      document.removeEventListener("click", onOutsideNavigationClick, true);
  }, [hasUnsavedChanges]);

  React.useEffect(() => {
    if (!pendingLeave) return undefined;
    keepEditingRef.current?.focus();
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeLeaveModal();
      }
    };
    document.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = priorOverflow;
      document.removeEventListener("keydown", onEscape);
    };
  }, [pendingLeave, closeLeaveModal]);

  const dashboardSummary =
    parentContext?.dashboardSummary || parentContext?.dashboard_summary;

  React.useEffect(() => {
    let cancelled = false;

    async function loadDossierFromBackend() {
      if (!dossierStorageKey || !dossierMetaStorageKey) {
        return;
      }

      setDraftLoadState({ ready: false, error: null });

      // Local cache is a fallback only. If it carries an actual dossier ID,
      // always fetch the canonical DB record before showing an editable form.
      let cachedDraft = null;
      try {
        const raw = localStorage.getItem(dossierStorageKey);
        cachedDraft = raw ? JSON.parse(raw) : null;
      } catch {
        localStorage.removeItem(dossierStorageKey);
      }
      const backendDossierId =
        location.state?.dossierId ||
        location.state?.dossier_id ||
        dashboardSummary?.dossier_id ||
        cachedDraft?.dossier_id ||
        null;

      /*
       * No authenticated backend dossier yet:
       * restore only this Auth0 user's own local draft.
       */
      if (!backendDossierId) {
        // A purely local draft has not yet been saved to Django.
        lastSavedFingerprintRef.current = dossierEditableFingerprint(
          DEFAULT_DOSSIER_DRAFT,
        );
        try {
          const saved = localStorage.getItem(dossierStorageKey);

          if (!saved) {
            if (!cancelled) {
              lastSavedFingerprintRef.current = dossierEditableFingerprint(
                DEFAULT_DOSSIER_DRAFT,
              );
              setDossierDraft(DEFAULT_DOSSIER_DRAFT);
              setDraftLoadState({ ready: true, error: null });
            }

            return;
          }

          const parsed = JSON.parse(saved);

          if (!cancelled) {
            const restored = normalizeStoredDraft(parsed);
            lastSavedFingerprintRef.current =
              dossierEditableFingerprint(restored);
            setDossierDraft(restored);
            setDraftLoadState({ ready: true, error: null });
          }
        } catch (error) {
          console.error("Failed to restore scoped dossier draft:", error);

          localStorage.removeItem(dossierStorageKey);
          localStorage.removeItem(dossierMetaStorageKey);

          if (!cancelled) {
            lastSavedFingerprintRef.current = dossierEditableFingerprint(
              DEFAULT_DOSSIER_DRAFT,
            );
            setDossierDraft(DEFAULT_DOSSIER_DRAFT);
            setDraftLoadState({ ready: true, error: null });
          }
        }

        return;
      }

      try {
        const accessToken = await getAccessTokenSilently();

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/homeowner/dossiers/${backendDossierId}/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        /*
         * A 403/404 means this dossier is not accessible to the
         * authenticated Homebuyer. Never keep rendering cached data
         * for that dossier.
         */
        if (res.status === 403 || res.status === 404) {
          localStorage.removeItem(dossierStorageKey);
          localStorage.removeItem(dossierMetaStorageKey);

          if (!cancelled) {
            lastSavedFingerprintRef.current = dossierEditableFingerprint(
              DEFAULT_DOSSIER_DRAFT,
            );
            setDossierDraft(DEFAULT_DOSSIER_DRAFT);
            setDraftLoadState({ ready: true, error: null });
          }

          return;
        }

        if (!res.ok) {
          const errorPayload = await res.json().catch(() => ({}));

          throw new Error(
            errorPayload?.detail || `Could not load dossier (${res.status}).`,
          );
        }

        const data = await res.json();

        const mappedBackendDraft = mapDossierDetailToDraft(data);

        /*
         * Reuse local unsaved values only when they belong to the
         * same authenticated user AND the exact same backend dossier.
         */
        const localDraft = (() => {
          try {
            const saved = localStorage.getItem(dossierStorageKey);

            if (!saved) {
              return null;
            }

            const parsed = JSON.parse(saved);

            const localDossierId = Number(parsed?.dossier_id);
            const serverDossierId = Number(backendDossierId);

            if (
              !Number.isFinite(localDossierId) ||
              !Number.isFinite(serverDossierId) ||
              localDossierId !== serverDossierId
            ) {
              return null;
            }

            return parsed;
          } catch {
            return null;
          }
        })();

        const hydratedDraft = localDraft
          ? {
              ...DEFAULT_DOSSIER_DRAFT,
              ...mappedBackendDraft,

              /*
               * Local data may preserve unsaved edits, but only for
               * this same user/dossier. Ownership/market identifiers
               * always come from the authenticated backend response.
               */
              ...localDraft,

              dossier_id: mappedBackendDraft.dossier_id || backendDossierId,

              dossier_code:
                mappedBackendDraft.dossier_code ||
                localDraft.dossier_code ||
                null,

              borrowers: normalizeBorrowersByMaritalStatus(
                mergeBorrowers(
                  mappedBackendDraft.borrowers || [],
                  localDraft.borrowers || [],
                ),
              ),

              country_of_property_purchase:
                mappedBackendDraft.country_of_property_purchase || "",

              market_region_id: mappedBackendDraft.market_region_id ?? null,

              market_region: mappedBackendDraft.market_region || null,

              dossier_financial_info: mergePreservingExisting(
                mappedBackendDraft.dossier_financial_info,
                localDraft.dossier_financial_info,
              ),

              property_information: mergePreservingExisting(
                mappedBackendDraft.property_information,
                localDraft.property_information,
              ),

              document_requirements:
                mappedBackendDraft.document_requirements || [],

              document_requirement_map_by_code:
                mappedBackendDraft.document_requirement_map_by_code || {},
            }
          : {
              ...DEFAULT_DOSSIER_DRAFT,
              ...mappedBackendDraft,

              dossier_financial_info: {
                ...DEFAULT_DOSSIER_DRAFT.dossier_financial_info,
                ...(mappedBackendDraft.dossier_financial_info || {}),
              },

              property_information: {
                ...DEFAULT_DOSSIER_DRAFT.property_information,
                ...(mappedBackendDraft.property_information || {}),
              },

              review_download: {
                ...DEFAULT_DOSSIER_DRAFT.review_download,
                ...(mappedBackendDraft.review_download || {}),
              },

              borrowers: normalizeBorrowersByMaritalStatus(
                mappedBackendDraft.borrowers || [],
              ),

              document_requirements:
                mappedBackendDraft.document_requirements || [],

              document_requirement_map_by_code:
                mappedBackendDraft.document_requirement_map_by_code || {},
            };

        if (cancelled) {
          return;
        }

        // Backend record is the last saved baseline; local overrides remain dirty.
        lastSavedFingerprintRef.current =
          dossierEditableFingerprint(mappedBackendDraft);
        dossierDraftRef.current = hydratedDraft;
        setDossierDraft(hydratedDraft);
        setDraftLoadState({ ready: true, error: null });

        localStorage.setItem(dossierStorageKey, JSON.stringify(hydratedDraft));

        // Retain progress metadata for the same dossier instead of erasing
        // current_step_key every time the form is loaded.
        let previousMeta = null;
        try {
          const rawMeta = localStorage.getItem(dossierMetaStorageKey);
          previousMeta = rawMeta ? JSON.parse(rawMeta) : null;
        } catch {
          previousMeta = null;
        }

        const resolvedDossierId =
          mappedBackendDraft.dossier_id || backendDossierId;
        const sameDossier =
          previousMeta?.dossier_id != null &&
          String(previousMeta.dossier_id) === String(resolvedDossierId);

        const backendStepCode = String(
          data?.current_step_code || dashboardSummary?.current_stage || "",
        ).toUpperCase();
        const backendStepKey = DOSSIER_STEP_KEY_BY_CODE[backendStepCode];

        const savedStepKey = sameDossier
          ? previousMeta?.current_step_key
          : null;
        const persistedStepKey =
          (savedStepKey && DOSSIER_STEP_CODE_BY_KEY[savedStepKey]
            ? savedStepKey
            : null) ||
          backendStepKey ||
          "personal-info";
        const persistedStep = STEPS.find(
          (step) => step.key === persistedStepKey,
        );

        localStorage.setItem(
          dossierMetaStorageKey,
          JSON.stringify({
            ...(sameDossier ? previousMeta : {}),
            status: "DRAFT",
            dossier_id: resolvedDossierId,
            dossier_code: mappedBackendDraft.dossier_code || null,
            current_step_key: persistedStep?.key || "personal-info",
            current_step_code:
              DOSSIER_STEP_CODE_BY_KEY[persistedStep?.key] || "PERSONAL",
            current_stage:
              DOSSIER_STEP_CODE_BY_KEY[persistedStep?.key] || "PERSONAL",
            current_step_path:
              persistedStep?.path || "/hb/dossier/form/personal-info",
            current_step_label: persistedStep?.label || STEPS[0].label,
            updated_at:
              (sameDossier && previousMeta?.updated_at) ||
              data?.updated_at ||
              new Date().toISOString(),
          }),
        );
      } catch (error) {
        console.error("Could not load authenticated Homebuyer dossier:", error);
        if (!cancelled) {
          setDraftLoadState({
            ready: false,
            error: error?.message || "Could not load the dossier.",
          });
        }
      }
    }

    loadDossierFromBackend();

    return () => {
      cancelled = true;
    };
  }, [
    getAccessTokenSilently,
    location.state?.dossierId,
    location.state?.dossier_id,
    dashboardSummary?.dossier_id,
    dossierStorageKey,
    dossierMetaStorageKey,
    draftReloadNonce,
    dashboardSummary?.current_stage,
    STEPS,
  ]);

  // One completion contract shared with the dashboard. Nonempty invalid values
  // (including IBAN, dates and unverified addresses) never count as complete.
  const dossierStepChecks = React.useMemo(
    () => validateDossierStepCompletion(dossierDraft),
    [dossierDraft],
  );
  const draftStepCompletion = React.useMemo(
    () =>
      Object.fromEntries(
        Object.entries(dossierStepChecks).map(([key, result]) => [
          key,
          result.isValid,
        ]),
      ),
    [dossierStepChecks],
  );

  const [stepValidity, setStepValidity] = React.useState({});
  const [saveModalOpen, setSaveModalOpen] = React.useState(false);
  const [isRunningNext, setIsRunningNext] = React.useState(false);
  const [isSavingDraft, setIsSavingDraft] = React.useState(false);

  const [downloadAlert, setDownloadAlert] = React.useState({
    open: false,
    missingGroups: [],
  });

  const missingModalCloseRef = React.useRef(null);
  const closeMissingModal = React.useCallback(() => {
    setDownloadAlert({ open: false, missingGroups: [] });
  }, []);

  const localizedMissingGroups = React.useMemo(() => {
    const requirements = dossierDraft?.document_requirements || [];
    return downloadAlert.missingGroups
      .map((group) => {
        const requiredMissingKeys = new Set(
          group.fields
            .map((raw) => MISSING_MODAL_SPECIAL_ISSUES[raw] || raw)
            .filter((key) => /^[a-z][a-z0-9_]*$/.test(key)),
        );
        const issues = group.fields
          .map((field) =>
            resolveMissingModalIssue(field, group.key, requirements, t),
          )
          // Do not show both "required" and "invalid" for the same EMPTY field.
          .filter(
            (issue) =>
              !(issue.invalid && requiredMissingKeys.has(issue.fieldKey)),
          );
        return {
          key: group.key,
          label: t(MISSING_MODAL_STEP_LABEL_KEYS[group.key] || "", {
            defaultValue: group.step,
          }),
          issues,
        };
      })
      .filter((group) => group.issues.length > 0);
  }, [downloadAlert.missingGroups, dossierDraft?.document_requirements, t]);

  const missingModalIssueCount = localizedMissingGroups.reduce(
    (total, group) => total + group.issues.length,
    0,
  );

  React.useEffect(() => {
    if (!downloadAlert.open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    missingModalCloseRef.current?.focus();
    const onEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMissingModal();
      }
    };
    document.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onEscape);
    };
  }, [downloadAlert.open, closeMissingModal]);

  const [downloadModalOpen, setDownloadModalOpen] = React.useState(false);
  const [isPreparingDownload, setIsPreparingDownload] = React.useState(false);
  const [excelLanguage, setExcelLanguage] = React.useState("en");

  const [isDownloadModalClosing, setIsDownloadModalClosing] =
    React.useState(false);

  const closeDownloadModal = React.useCallback(() => {
    if (isDownloadModalClosing) return;

    setIsDownloadModalClosing(true);

    window.setTimeout(() => {
      setDownloadModalOpen(false);
      setIsDownloadModalClosing(false);
    }, 220);
  }, [isDownloadModalClosing]);

  const openDownloadModal = React.useCallback(() => {
    setShowLenders(false);

    if (window.scrollY <= 2) {
      setDownloadModalOpen(true);
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    const waitForTop = () => {
      if (window.scrollY <= 2) {
        setDownloadModalOpen(true);
        return;
      }

      window.requestAnimationFrame(waitForTop);
    };

    window.requestAnimationFrame(waitForTop);
  }, []);

  const dossierDraftRef = React.useRef(dossierDraft);
  const stepValidityRef = React.useRef(stepValidity);

  React.useEffect(() => {
    dossierDraftRef.current = dossierDraft;
  }, [dossierDraft]);

  /* =========================================================
   AUTO-PERSIST DOSSIER DRAFT
   ========================================================= */

  React.useEffect(() => {
    if (!dossierStorageKey || !draftLoadState.ready) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      try {
        localStorage.setItem(dossierStorageKey, JSON.stringify(dossierDraft));
      } catch (error) {
        console.error("Failed to persist dossier draft:", error);
      }
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dossierDraft, dossierStorageKey, draftLoadState.ready]);

  React.useEffect(() => {
    stepValidityRef.current = stepValidity;
  }, [stepValidity]);

  const stepSubmitHandlersRef = React.useRef({});

  const currentStepIndex = STEPS.findIndex(
    (step) => step.path === location.pathname,
  );

  const currentStep = STEPS[currentStepIndex] || STEPS[0];
  const currentStepKey = currentStep.key;
  const currentSectionKey = STEP_SECTION_MAP[currentStepKey];

  const canGoBack = currentStepIndex > 0;
  const canGoNext = currentStepIndex < STEPS.length - 1;
  const currentStepValid = stepValidity[currentStepKey]?.isValid ?? false;

  const mergeStepValuesIntoDraft = React.useCallback(
    (draft, stepKey, values) => {
      if (!values) return draft;

      if (stepKey === "personal-info") {
        const mergedBorrowers = values.borrowers || [];

        return {
          ...draft,
          borrowers: mergedBorrowers,
          co_dossier_applicant_id: mergedBorrowers.some(
            (borrower) => borrower.role === "CO_APPLICANT",
          )
            ? draft.co_dossier_applicant_id
            : null,
        };
      }

      if (stepKey === "financial-info") {
        return {
          ...draft,
          borrowers: mergeBorrowers(
            draft.borrowers || [],
            values.borrowers || [],
          ),
          dossier_financial_info: mergePreservingExisting(
            draft.dossier_financial_info,
            values.dossier_financial_info,
          ),
        };
      }

      if (stepKey === "documents") {
        return {
          ...draft,
          borrowers: mergeBorrowers(
            draft.borrowers || [],
            values.borrowers || [],
          ),
          document_requirements:
            values.document_requirements || draft.document_requirements,
          document_requirement_map_by_code:
            values.document_requirement_map_by_code ||
            draft.document_requirement_map_by_code,
        };
      }

      if (stepKey === "extracted-data") {
        return {
          ...draft,
          borrowers: mergeBorrowers(
            draft.borrowers || [],
            values.borrowers || [],
          ),
        };
      }

      if (stepKey === "property-information") {
        return {
          ...draft,
          property_information: mergePreservingExisting(
            draft.property_information,
            values,
          ),
        };
      }

      if (stepKey === "review-download") {
        return {
          ...draft,
          review_download: mergePreservingExisting(
            draft.review_download,
            values,
          ),
        };
      }

      return draft;
    },
    [],
  );

  const computedRequirementMapByCode = React.useMemo(() => {
    if (
      dossierDraft?.document_requirement_map_by_code &&
      Object.keys(dossierDraft.document_requirement_map_by_code).length > 0
    ) {
      return dossierDraft.document_requirement_map_by_code;
    }

    const requirements = dossierDraft?.document_requirements || [];
    return Object.fromEntries(
      requirements.map((req) => [req.document_type_code, req.id]),
    );
  }, [
    dossierDraft?.document_requirement_map_by_code,
    dossierDraft?.document_requirements,
  ]);

  const handleStepValidityChange = React.useCallback(
    (stepKey, isValid, errors = {}, values = {}) => {
      // Step components often keep edits in their own form state until Save/Next.
      // Include those live values in the guard, not just dossierDraft state.
      if (stepKey === currentStepKey) {
        latestDraftFingerprintRef.current = dossierEditableFingerprint(
          mergeStepValuesIntoDraft(dossierDraftRef.current, stepKey, values),
        );
      }
      setStepValidity((prev) => ({
        ...prev,
        [stepKey]: { isValid, errors, values },
      }));
    },
    [currentStepKey, mergeStepValuesIntoDraft],
  );

  React.useEffect(() => {
    const liveValues = stepValidity[currentStepKey]?.values;
    latestDraftFingerprintRef.current = dossierEditableFingerprint(
      liveValues
        ? mergeStepValuesIntoDraft(dossierDraft, currentStepKey, liveValues)
        : dossierDraft,
    );
  }, [dossierDraft, currentStepKey, stepValidity, mergeStepValuesIntoDraft]);

  const updateDraftSection = React.useCallback((sectionKey, values) => {
    setDossierDraft((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        ...values,
      },
    }));
  }, []);

  const setBorrowersDraft = React.useCallback((borrowersOrUpdater) => {
    setDossierDraft((prev) => {
      const nextBorrowers =
        typeof borrowersOrUpdater === "function"
          ? borrowersOrUpdater(prev.borrowers || [])
          : borrowersOrUpdater;

      const finalBorrowers = Array.isArray(nextBorrowers)
        ? nextBorrowers
        : prev.borrowers || [];

      const hasCoApplicant = finalBorrowers.some(
        (borrower) => borrower.role === "CO_APPLICANT",
      );

      if (
        JSON.stringify(prev.borrowers || []) === JSON.stringify(finalBorrowers)
      ) {
        return prev;
      }

      return {
        ...prev,
        borrowers: finalBorrowers,
        co_dossier_applicant_id: hasCoApplicant
          ? prev.co_dossier_applicant_id
          : null,
      };
    });
  }, []);

  const updateBorrowerSection = React.useCallback(
    (borrowerRole, sectionKey, values) => {
      setDossierDraft((prev) => ({
        ...prev,
        borrowers: (prev.borrowers || []).map((borrower) =>
          borrower.role === borrowerRole
            ? {
                ...borrower,
                [sectionKey]: {
                  ...(borrower[sectionKey] || {}),
                  ...values,
                },
              }
            : borrower,
        ),
      }));
    },
    [],
  );

  const updateDraftMetaFields = React.useCallback((values) => {
    setDossierDraft((prev) => {
      const merged = {
        ...prev,
        ...values,
      };

      if (
        values?.document_requirements &&
        (!values.document_requirement_map_by_code ||
          Object.keys(values.document_requirement_map_by_code).length === 0)
      ) {
        merged.document_requirement_map_by_code = Object.fromEntries(
          values.document_requirements.map((req) => [
            req.document_type_code,
            req.id,
          ]),
        );
      }

      return merged;
    });
  }, []);

  const persistDraftMeta = React.useCallback(
    (overrides = {}) => {
      const resolvedStepKey = overrides?.current_step_key || currentStepKey;
      const resolvedStepCode =
        DOSSIER_STEP_CODE_BY_KEY[resolvedStepKey] || "PERSONAL";

      const draftMeta = {
        status: "DRAFT",
        dossier_id: dossierDraft?.dossier_id || null,
        dossier_code: dossierDraft?.dossier_code || null,
        dossier_applicant_id: dossierDraft?.dossier_applicant_id || null,
        current_step_key: resolvedStepKey,
        current_step_path: currentStep.path,
        current_step_label: currentStep.label,
        updated_at: new Date().toISOString(),
        ...overrides,
        // Derive both aliases from the destination step, not the previous one.
        current_step_code: resolvedStepCode,
        current_stage: resolvedStepCode,
      };

      if (!dossierMetaStorageKey) {
        return;
      }

      localStorage.setItem(dossierMetaStorageKey, JSON.stringify(draftMeta));
    },
    [dossierDraft, currentStep, currentStepKey, dossierMetaStorageKey],
  );

  // Persist the resume position in Django. localStorage alone cannot update
  // /api/homeowner/me dashboard_summary.current_stage (it reads the database).
  const persistBackendCurrentStep = React.useCallback(
    async (dossierId, stepKey) => {
      const stepCode = DOSSIER_STEP_CODE_BY_KEY[stepKey];
      if (!dossierId || !stepCode) {
        throw new Error("A valid dossier and dossier step are required.");
      }

      const accessToken = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/homeowner/dossiers/${encodeURIComponent(String(dossierId))}/current-step/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ step_code: stepCode }),
        },
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.detail ||
            payload?.step_code ||
            `Could not save the current dossier step (${response.status}).`,
        );
      }
      if (payload?.current_step_code !== stepCode) {
        throw new Error("The server did not confirm the saved dossier step.");
      }
    },
    [getAccessTokenSilently],
  );

  const registerStepSubmitHandler = React.useCallback((stepKey, handler) => {
    if (handler) {
      stepSubmitHandlersRef.current[stepKey] = handler;
    } else {
      delete stepSubmitHandlersRef.current[stepKey];
    }
  }, []);

  const runCurrentStepBeforeNavigation = React.useCallback(
    async ({
      targetStep = null,
      validateBeforeDownload = false,
      skipCurrentStepSubmit = false,
    }) => {
      const latestDraft = dossierDraftRef.current;
      const latestStepValidity = stepValidityRef.current;

      const currentStepValues = latestStepValidity[currentStepKey]?.values;
      let mergedDraft = latestDraft;

      if (currentStepValues) {
        mergedDraft = mergeStepValuesIntoDraft(
          latestDraft,
          currentStepKey,
          currentStepValues,
        );
      }

      const customSubmitHandler = stepSubmitHandlersRef.current[currentStepKey];

      /*
       * Review confirmation is a final-download requirement, not a navigation
       * requirement. When leaving Review for an earlier step, current values
       * are still merged/persisted above, but its confirmation-gated submit
       * handler is intentionally skipped.
       */
      if (customSubmitHandler && !skipCurrentStepSubmit) {
        const result = await customSubmitHandler({
          currentValues: currentStepValues || {},
          fullDraft: mergedDraft,
        });

        if (result?.ok === false) {
          showToast({
            type: "error",
            title: "Could not save dossier step",
            message: result?.error || "Please check the step and try again.",
          });
          return { ok: false };
        }

        if (result?.updatedDraft) {
          mergedDraft = {
            ...mergedDraft,
            ...result.updatedDraft,
            borrowers: mergeBorrowers(
              mergedDraft.borrowers || [],
              result.updatedDraft.borrowers || [],
            ),
            dossier_financial_info: mergePreservingExisting(
              mergedDraft.dossier_financial_info,
              result.updatedDraft.dossier_financial_info,
            ),
            property_information: mergePreservingExisting(
              mergedDraft.property_information,
              result.updatedDraft.property_information,
            ),
          };
        }
      }

      if (validateBeforeDownload && targetStep?.key === "review-download") {
        const downloadValidation = validateDossierForDownload(mergedDraft);

        if (!downloadValidation.isValid) {
          setDownloadAlert({
            open: true,
            missingGroups: downloadValidation.missingGroups,
          });

          return { ok: false };
        }
      }

      setDossierDraft(mergedDraft);
      dossierDraftRef.current = mergedDraft;

      persistScopedDraft(mergedDraft);
      if (customSubmitHandler) {
        lastSavedFingerprintRef.current =
          dossierEditableFingerprint(mergedDraft);
      }

      return {
        ok: true,
        draft: mergedDraft,
      };
    },
    [currentStepKey, mergeStepValuesIntoDraft, persistScopedDraft, showToast],
  );

  const handleBack = async () => {
    if (isRunningNext) return;

    setIsRunningNext(true);

    try {
      const targetStep = canGoBack ? STEPS[currentStepIndex - 1] : null;

      const result = await runCurrentStepBeforeNavigation({
        targetStep,
        validateBeforeDownload: false,
        skipCurrentStepSubmit: currentStepKey === "review-download",
      });

      if (!result?.ok) return;

      const nextDraft = result.draft || dossierDraft;

      persistScopedDraft(nextDraft);

      persistDraftMeta({
        dossier_id: nextDraft?.dossier_id || null,
        dossier_code: nextDraft?.dossier_code || null,
        dossier_applicant_id:
          nextDraft?.primary_dossier_applicant_id ||
          nextDraft?.dossier_applicant_id ||
          null,
        current_step_key: targetStep?.key || currentStepKey,
        current_step_path: targetStep?.path || currentStep.path,
        current_step_label: targetStep?.label || currentStep.label,
      });

      if (canGoBack) {
        navigate(targetStep.path);
      } else {
        navigate("/hb/dossier");
      }
    } finally {
      setIsRunningNext(false);
    }
  };

  const handleNext = async () => {
    if (!canGoNext || isRunningNext) return;

    setIsRunningNext(true);

    try {
      const nextStep = STEPS[currentStepIndex + 1];

      const result = await runCurrentStepBeforeNavigation({
        targetStep: nextStep,
        validateBeforeDownload: true,
      });

      if (!result?.ok) return;

      persistDraftMeta({
        dossier_id:
          result.draft?.dossier_id || dossierDraft?.dossier_id || null,
        dossier_code:
          result.draft?.dossier_code || dossierDraft?.dossier_code || null,
        dossier_applicant_id:
          result.draft?.primary_dossier_applicant_id ||
          result.draft?.dossier_applicant_id ||
          null,
        current_step_key: nextStep?.key || currentStepKey,
        current_step_path: nextStep?.path || currentStep.path,
        current_step_label: nextStep?.label || currentStep.label,
      });

      navigate(nextStep.path);
    } finally {
      setIsRunningNext(false);
    }
  };

  const handleDownloadRequest = React.useCallback(async () => {
    const currentStepValues = stepValidity["review-download"]?.values;
    let mergedDraft = dossierDraft;

    if (currentStepValues) {
      mergedDraft = mergeStepValuesIntoDraft(
        dossierDraft,
        "review-download",
        currentStepValues,
      );
      setDossierDraft(mergedDraft);
    }

    const reviewDownloadHandler =
      stepSubmitHandlersRef.current["review-download"];

    if (reviewDownloadHandler) {
      const result = await reviewDownloadHandler({
        currentValues: currentStepValues || {},
        fullDraft: mergedDraft,
      });

      if (!result?.ok) return;

      if (result?.updatedDraft) {
        mergedDraft = result.updatedDraft;
        setDossierDraft(result.updatedDraft);
      }
    }

    const validation = validateDossierForDownload(mergedDraft);

    if (!validation.isValid) {
      setDownloadAlert({
        open: true,
        missingGroups: validation.missingGroups,
      });
      return;
    }

    try {
      setIsPreparingDownload(true);

      const accessToken = await getAccessTokenSilently();

      setIsLoadingLenders(true);

      try {
        const lenders = await fetchParticipatingLenders({
          accessToken,
          marketRegionId: mergedDraft?.market_region_id,
        });

        setParticipatingLenders(lenders);
      } catch (error) {
        setParticipatingLenders([]);

        showToast({
          type: "error",
          title: t(
            "toast.lendersLoadErrorTitle",
            "Participating lenders unavailable",
          ),
          message:
            error?.message ||
            t(
              "toast.lendersLoadErrorMessage",
              "The participating lenders could not be loaded.",
            ),
        });
      } finally {
        setIsLoadingLenders(false);
      }

      const prepared = await prepareDossierDownload({
        dossierId: mergedDraft.dossier_id,
        accessToken,
      });

      const nextDraft = {
        ...mergedDraft,
        status: prepared.status || "READY_FOR_EXPORT",
        dossier_code: prepared.dossier_code || mergedDraft.dossier_code,
        review_download: {
          ...(mergedDraft.review_download || {}),
          dossier_reference_code:
            prepared.dossier_reference_code ||
            prepared.dossier_code ||
            mergedDraft.dossier_code,
          prepared_at: prepared.prepared_at || new Date().toISOString(),
        },
      };

      setDossierDraft(nextDraft);
      dossierDraftRef.current = nextDraft;

      persistScopedDraft(nextDraft);

      openDownloadModal();
    } catch (error) {
      showToast({
        type: "error",
        title: t("toast.prepareErrorTitle", "Could not prepare dossier"),
        message:
          error?.message ||
          t(
            "errors.prepareDossier",
            "The dossier could not be prepared for download.",
          ),
      });
    } finally {
      setIsPreparingDownload(false);
    }
  }, [
    dossierDraft,
    mergeStepValuesIntoDraft,
    stepValidity,
    getAccessTokenSilently,
    showToast,
    openDownloadModal,
    persistScopedDraft,
    t,
  ]);

  const handleConfirmSaveDraft = React.useCallback(async () => {
    if (isSavingDraft) return;
    setIsSavingDraft(true);

    try {
      const result = await runCurrentStepBeforeNavigation({
        targetStep: currentStep,
        validateBeforeDownload: false,

        // Review confirmation and Terms & Conditions apply only to final
        // preparation/download, never to saving an unfinished draft.
        skipCurrentStepSubmit: currentStepKey === "review-download",
      });

      if (!result?.ok) return;

      const nextDraft = result.draft || dossierDraftRef.current;
      const savedDossierId = nextDraft?.dossier_id;

      // Save the CURRENT step, not the first incomplete step. This must finish
      // before refreshMe() reads the dashboard summary from the backend.
      if (savedDossierId) {
        await persistBackendCurrentStep(savedDossierId, currentStepKey);
      }
      // A draft not initialized in Django yet is saved in scoped localStorage.
      // Once the personal step creates the dossier, subsequent saves sync it.

      setDossierDraft(nextDraft);
      dossierDraftRef.current = nextDraft;
      persistScopedDraft(nextDraft);

      persistDraftMeta({
        dossier_id: savedDossierId,
        dossier_code: nextDraft?.dossier_code || null,
        dossier_applicant_id:
          nextDraft?.primary_dossier_applicant_id ||
          nextDraft?.dossier_applicant_id ||
          null,
        current_step_key: currentStepKey,
        current_step_code: DOSSIER_STEP_CODE_BY_KEY[currentStepKey],
        current_step_path: currentStep.path,
        current_step_label: currentStep.label,
      });

      setSaveModalOpen(false);

      try {
        await Promise.resolve(refreshMe?.());
      } catch (error) {
        console.error("Could not refresh Homebuyer dashboard:", error);
      }
      suppressLeavePromptRef.current = true;
      navigate("/hb/dashboard");
    } catch (error) {
      console.error("Could not persist the current dossier step:", error);
      showToast({
        type: "error",
        title: "Could not save dossier progress",
        message:
          error?.message ||
          "Your saved fields were retained, but the current step could not be updated. Please try again.",
      });
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    isSavingDraft,
    refreshMe,
    runCurrentStepBeforeNavigation,
    currentStep,
    currentStepKey,
    persistBackendCurrentStep,
    persistDraftMeta,
    persistScopedDraft,
    navigate,
    showToast,
  ]);

  // React.useEffect(() => {
  //   if (
  //     currentStepKey === "extracted-data" &&
  //     dossierDraft?.requires_reextraction
  //   ) {
  //     navigate("/hb/dossier/form/documents", { replace: true });
  //   }
  // }, [currentStepKey, dossierDraft?.requires_reextraction, navigate]);

  const handleStepNavigation = React.useCallback(
    async (e, step) => {
      if (step.path === location.pathname || isRunningNext) return;

      e.preventDefault();
      setIsRunningNext(true);

      try {
        const targetStepIndex = STEPS.findIndex(
          (candidateStep) => candidateStep.key === step.key,
        );

        const isMovingToEarlierStep =
          targetStepIndex >= 0 && targetStepIndex < currentStepIndex;

        const result = await runCurrentStepBeforeNavigation({
          targetStep: step,
          validateBeforeDownload: step.key === "review-download",
          skipCurrentStepSubmit:
            currentStepKey === "review-download" && isMovingToEarlierStep,
        });

        if (!result?.ok) return;

        const nextDraft = result.draft || dossierDraft;

        persistScopedDraft({
          ...nextDraft,
          document_requirement_map_by_code: computedRequirementMapByCode,
        });

        persistDraftMeta({
          dossier_id: nextDraft?.dossier_id || null,
          dossier_code: nextDraft?.dossier_code || null,
          dossier_applicant_id:
            nextDraft?.primary_dossier_applicant_id ||
            nextDraft?.dossier_applicant_id ||
            null,
          current_step_key: step.key,
          current_step_path: step.path,
          current_step_label: step.label,
        });

        navigate(step.path);
      } finally {
        setIsRunningNext(false);
      }
    },
    [
      location.pathname,
      isRunningNext,
      runCurrentStepBeforeNavigation,
      dossierDraft,
      computedRequirementMapByCode,
      persistDraftMeta,
      persistScopedDraft,
      currentStepIndex,
      currentStepKey,
      STEPS,
      navigate,
    ],
  );

  const handleConfirmDownload = React.useCallback(async () => {
    try {
      setIsPreparingDownload(true);

      const currentStepValues = stepValidity["review-download"]?.values;
      let mergedDraft = dossierDraft;

      if (currentStepValues) {
        mergedDraft = mergeStepValuesIntoDraft(
          dossierDraft,
          "review-download",
          currentStepValues,
        );
        setDossierDraft(mergedDraft);
      }

      const validation = validateDossierForDownload(mergedDraft);

      if (!validation.isValid) {
        showToast({
          type: "error",
          title: t("toast.incompleteDossierTitle", "Dossier is incomplete"),
          message: t(
            "errors.incompleteDossier",
            "Complete the required dossier information before downloading.",
          ),
        });

        return;
      }

      const accessToken = await getAccessTokenSilently();

      console.log("ZIP DOWNLOAD CLICKED", mergedDraft.dossier_id);

      await downloadDossierZip({
        dossierId: mergedDraft.dossier_id,
        accessToken,
        lang: excelLanguage,
      });

      const nextDraft = {
        ...mergedDraft,
        status: "EXPORTED",
        review_download: {
          ...(mergedDraft.review_download || {}),
          downloaded_at: new Date().toISOString(),
        },
      };

      setDossierDraft(nextDraft);
      dossierDraftRef.current = nextDraft;

      persistScopedDraft(nextDraft);

      showToast({
        type: "success",
        title: t("toast.downloadSuccessTitle", "Dossier downloaded"),
        message: t(
          "toast.downloadSuccessMessage",
          "Your LEG-UP dossier was exported successfully.",
        ),
      });

      Promise.resolve(refreshMe?.()).catch(() => {});
      closeDownloadModal();
    } catch (error) {
      showToast({
        type: "error",
        title: t("toast.downloadErrorTitle", "Dossier download failed"),
        message:
          error?.message ||
          t("errors.downloadDossier", "The dossier could not be downloaded."),
      });
    } finally {
      setIsPreparingDownload(false);
    }
  }, [
    dossierDraft,
    mergeStepValuesIntoDraft,
    stepValidity,
    refreshMe,
    getAccessTokenSilently,
    excelLanguage,
    showToast,
    closeDownloadModal,
    persistScopedDraft,
    t,
  ]);

  const draftForOutlet = {
    ...dossierDraft,
    document_requirement_map_by_code: computedRequirementMapByCode,
  };

  const dossierReferenceCode =
    dossierDraft?.review_download?.dossier_reference_code ||
    dossierDraft?.dossier_code ||
    "LEG-UP-DRAFT";

  React.useEffect(() => {
    if (!downloadModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [downloadModalOpen]);

  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && downloadModalOpen) {
        closeDownloadModal();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [downloadModalOpen, closeDownloadModal]);

  if (draftLoadState.error) {
    return (
      <div className="hb-app-flow" role="alert" style={{ padding: 32 }}>
        <p>{draftLoadState.error}</p>
        <button
          type="button"
          onClick={() => setDraftReloadNonce((value) => value + 1)}
        >
          Retry loading dossier
        </button>
      </div>
    );
  }

  if (!draftLoadState.ready) {
    return (
      <div className="hb-app-flow" role="status" style={{ padding: 32 }}>
        Loading dossier…
      </div>
    );
  }

  return (
    <div className="hb-app-flow">
      <div className="hb-app-flow__shell">
        <aside className="hb-app-flow__sidebar">
          <div className="hb-app-flow__steps">
            {STEPS.map((step) => {
              const isActive = location.pathname === step.path;
              const isComplete =
                draftStepCompletion[step.key] === true &&
                stepValidity[step.key]?.isValid !== false;

              let visualState = "incomplete";
              if (isComplete) {
                visualState = "complete";
              } else if (isActive) {
                visualState = "active";
              }

              return (
                <NavLink
                  key={step.path}
                  to={step.path}
                  onClick={(e) => handleStepNavigation(e, step)}
                  className={`hb-app-flow__step hb-app-flow__step--${visualState}`}
                >
                  <span className="hb-app-flow__step-number">
                    {step.number}
                  </span>
                  <span className="hb-app-flow__step-label">{step.label}</span>
                </NavLink>
              );
            })}
          </div>
        </aside>

        <section className="hb-app-flow__main">
          <div className="hb-app-flow__content">
            <div key={location.pathname} className="hb-app-flow__content-anim">
              <Outlet
                context={{
                  currentStepKey,
                  currentSectionKey,
                  currentStepValid,
                  handleStepValidityChange,
                  stepValidity,
                  dossierDraft: draftForOutlet,
                  updateDraftSection,
                  updateDraftMetaFields,
                  setBorrowersDraft,
                  updateBorrowerSection,
                  persistDraftMeta,
                  registerStepSubmitHandler,
                  dossierId: dossierDraft?.dossier_id || null,
                  dossierApplicantId:
                    dossierDraft?.dossier_applicant_id || null,
                  primaryDossierApplicantId:
                    dossierDraft?.primary_dossier_applicant_id || null,
                  coDossierApplicantId:
                    dossierDraft?.co_dossier_applicant_id || null,
                  documentRequirements:
                    dossierDraft?.document_requirements || [],
                  requiresReextraction:
                    dossierDraft?.requires_reextraction || false,
                  requirementMapByCode: computedRequirementMapByCode,
                  account,
                  profile,
                  openSaveModal: () => setSaveModalOpen(true),
                }}
              />
            </div>
          </div>

          <div className="hb-app-flow__footer">
            <button
              type="button"
              className="hb-app-flow__btn hb-app-flow__btn--back"
              onClick={handleBack}
            >
              {t("buttons.back")}
            </button>

            {currentStepKey === "review-download" ? (
              <button
                type="button"
                className="hb-app-flow__btn hb-app-flow__btn--next"
                onClick={handleDownloadRequest}
              >
                {t("buttons.downloadDossier", "Download dossier")}
              </button>
            ) : (
              <button
                type="button"
                className="hb-app-flow__btn hb-app-flow__btn--next"
                onClick={handleNext}
                disabled={!canGoNext || isRunningNext}
              >
                {isRunningNext ? "Saving..." : t("buttons.next")}
              </button>
            )}
          </div>
        </section>
      </div>

      {pendingLeave &&
        createPortal(
          <div
            className="hb-dossier-unsaved-backdrop"
            onMouseDown={(event) => {
              if (event.currentTarget === event.target) closeLeaveModal();
            }}
          >
            <section
              className="hb-dossier-unsaved-modal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="hb-dossier-unsaved-title"
              aria-describedby="hb-dossier-unsaved-description"
            >
              <div className="hb-dossier-unsaved-modal__heading">
                <span
                  className="hb-dossier-unsaved-modal__symbol"
                  aria-hidden="true"
                >
                  <TriangleAlert size={24} strokeWidth={2} />
                </span>
                <button
                  type="button"
                  className="hb-dossier-unsaved-modal__close"
                  onClick={closeLeaveModal}
                  aria-label={t("unsavedChanges.close", "Close")}
                >
                  <X size={20} />
                </button>
              </div>
              <h3 id="hb-dossier-unsaved-title">
                {t("unsavedChanges.title", "Unsaved changes")}
              </h3>
              <p id="hb-dossier-unsaved-description">
                {t(
                  "unsavedChanges.description",
                  "You have changes that have not been saved. If you leave this page now, those changes will be lost.",
                )}
              </p>
              <div className="hb-dossier-unsaved-modal__actions">
                <button
                  ref={keepEditingRef}
                  type="button"
                  className="hb-dossier-unsaved-modal__stay"
                  onClick={closeLeaveModal}
                >
                  {t("unsavedChanges.stay", "Keep editing")}
                </button>
                <button
                  type="button"
                  className="hb-dossier-unsaved-modal__leave"
                  onClick={confirmLeaveDossier}
                >
                  {t("unsavedChanges.leave", "Leave without saving")}
                </button>
              </div>
            </section>
          </div>,
          document.body,
        )}

      {saveModalOpen && (
        <div
          className="hb-save-modal__backdrop"
          onClick={() => setSaveModalOpen(false)}
        >
          <div className="hb-save-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hb-save-modal__icon-wrap">
              <div className="hb-save-modal__icon">
                <Save size={24} />
              </div>
            </div>

            <h3 className="hb-save-modal__title">{t("saveModal.title")}</h3>

            <p className="hb-save-modal__text">{t("saveModal.text")}</p>

            <div className="hb-save-modal__actions">
              <button
                type="button"
                className="hb-save-modal__btn hb-save-modal__btn--ghost"
                onClick={() => setSaveModalOpen(false)}
              >
                {t("saveModal.cancel")}
              </button>

              <button
                type="button"
                className="hb-save-modal__btn hb-save-modal__btn--primary"
                onClick={handleConfirmSaveDraft}
                disabled={isSavingDraft}
              >
                {isSavingDraft
                  ? t("buttons.saving", "Saving...")
                  : t("saveModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {downloadAlert.open &&
        createPortal(
          <div
            className="hb-dossier-missing-backdrop"
            onMouseDown={(event) => {
              if (event.currentTarget === event.target) closeMissingModal();
            }}
          >
            <section
              className="hb-dossier-missing-modal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="hb-dossier-missing-title"
              aria-describedby="hb-dossier-missing-description"
            >
              <header className="hb-dossier-missing-modal__header">
                <span
                  className="hb-dossier-missing-modal__icon"
                  aria-hidden="true"
                >
                  <TriangleAlert size={22} />
                </span>
                <button
                  ref={missingModalCloseRef}
                  type="button"
                  className="hb-dossier-missing-modal__close"
                  aria-label={t("unsavedChanges.close", "Close")}
                  onClick={closeMissingModal}
                >
                  <X size={19} />
                </button>
                <h3 id="hb-dossier-missing-title">{t("missingModal.title")}</h3>
                <p id="hb-dossier-missing-description">
                  {t("missingModal.text")}
                </p>
                <span className="hb-dossier-missing-modal__summary">
                  {t("missingModal.issuesCount", {
                    total: missingModalIssueCount,
                    defaultValue: "{{total}} items need your attention",
                  })}
                </span>
              </header>

              <div className="hb-dossier-missing-modal__scroll" tabIndex={0}>
                {localizedMissingGroups.map((group) => (
                  <section
                    className="hb-dossier-missing-modal__group"
                    key={group.key}
                  >
                    <div className="hb-dossier-missing-modal__group-header">
                      <h4>{group.label}</h4>
                      <span aria-label={`${group.issues.length}`}>
                        {group.issues.length}
                      </span>
                    </div>
                    <ul className="hb-dossier-missing-modal__issues">
                      {group.issues.map((issue, index) => (
                        <li key={`${group.key}-${index}`}>
                          <span
                            className="hb-dossier-missing-modal__issue-dot"
                            aria-hidden="true"
                          />
                          <span>{issue.text}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>

              <footer className="hb-dossier-missing-modal__footer">
                <button
                  type="button"
                  className="hb-dossier-missing-modal__button"
                  onClick={closeMissingModal}
                >
                  {t("missingModal.understood")}
                </button>
              </footer>
            </section>
          </div>,
          document.body,
        )}

      {downloadModalOpen ? (
        <div
          className={`hb-review-modal__backdrop ${
            isDownloadModalClosing ? "hb-review-modal__backdrop--closing" : ""
          }`}
          onClick={closeDownloadModal}
        >
          <div
            className={`hb-review-download-modal ${
              isDownloadModalClosing ? "hb-review-download-modal--closing" : ""
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hb-review-download-modal__title">
              <CheckCircle2 size={34} />
              <h3>{t("downloadModal.title")}</h3>
            </div>

            <p>{t("downloadModal.description")}</p>
            <div className="hb-review-download-modal__notice">
              <span>{t("downloadModal.notSubmittedPrefix")}</span>{" "}
              <strong>{t("downloadModal.notSubmittedStrong")}</strong>{" "}
              <span>{t("downloadModal.notSubmittedSuffix")}</span>
            </div>

            <h4>{t("downloadModal.whatCanYouDo")}</h4>

            <div className="hb-review-download-modal__items">
              <div>{t("downloadModal.item1")}</div>
              <div>{t("downloadModal.item2")}</div>
              <div>{t("downloadModal.item3")}</div>
              <div>{t("downloadModal.item4")}</div>
            </div>

            <div className="hb-review-download-modal__lender-box">
              <p>{t("downloadModal.lenderInfo")}</p>

              <button
                type="button"
                className="hb-review-lenders-toggle"
                onClick={() => setShowLenders((prev) => !prev)}
              >
                {showLenders
                  ? t("downloadModal.hideLenders")
                  : t("downloadModal.viewLenders")}
              </button>

              {showLenders ? (
                <div className="hb-review-lenders-expanded">
                  {isLoadingLenders ? (
                    <div className="hb-review-lender-disclaimer">
                      {t("downloadModal.loadingLenders")}
                    </div>
                  ) : participatingLenders.length > 0 ? (
                    <div className="hb-review-lender-list">
                      {participatingLenders.map((lender) => {
                        const regions = (lender.market_regions || [])
                          .map((region) => region.name)
                          .filter(Boolean)
                          .join(", ");

                        return lender.website_url ? (
                          <a
                            key={lender.id}
                            className="hb-review-lender-item hb-review-lender-item--link"
                            href={lender.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {lender.logo ? (
                              <img
                                src={lender.logo}
                                alt=""
                                className="hb-review-lender-item__logo"
                              />
                            ) : null}

                            <div className="hb-review-lender-item__content">
                              <strong className="hb-review-lender-item__name">
                                {lender.name}
                              </strong>

                              <span className="hb-review-lender-item__region">
                                {regions || lender.country_code || ""}
                              </span>
                            </div>

                            <span className="hb-review-lender-item__external">
                              ↗
                            </span>
                          </a>
                        ) : (
                          <div
                            key={lender.id}
                            className="hb-review-lender-item"
                          >
                            {lender.logo ? (
                              <img
                                src={lender.logo}
                                alt=""
                                className="hb-review-lender-item__logo"
                              />
                            ) : null}

                            <div className="hb-review-lender-item__content">
                              <strong className="hb-review-lender-item__name">
                                {lender.name}
                              </strong>

                              <span className="hb-review-lender-item__region">
                                {regions || lender.country_code || ""}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="hb-review-lender-disclaimer">
                      {t("downloadModal.noLenders")}
                    </div>
                  )}

                  <div className="hb-review-lender-disclaimer">
                    {t("downloadModal.lenderDisclaimer")}
                  </div>
                </div>
              ) : null}
            </div>
            <h4>{t("downloadModal.referenceCode")}</h4>

            <div className="hb-review-reference-code">
              <strong>{dossierReferenceCode}</strong>

              <button
                type="button"
                onClick={() =>
                  navigator.clipboard.writeText(dossierReferenceCode)
                }
              >
                {t("buttons.copy")}
              </button>
            </div>

            <div className="hb-review-download-modal__actions">
              <button
                type="button"
                className="hb-review-btn hb-review-btn--ghost"
                onClick={closeDownloadModal}
                disabled={isPreparingDownload}
              >
                {t("buttons.reviewDossier")}
              </button>

              <div className="hb-review-download-modal__download-side">
                <select
                  className="hb-review-language-select"
                  value={excelLanguage}
                  onChange={(e) => setExcelLanguage(e.target.value)}
                  disabled={isPreparingDownload}
                >
                  <option value="en">English</option>
                  <option value="nl">Dutch</option>
                  <option value="es">Spanish</option>
                  <option value="ca">Catalan</option>
                </select>

                <button
                  type="button"
                  className="hb-review-btn hb-review-btn--submit"
                  onClick={handleConfirmDownload}
                  disabled={isPreparingDownload}
                >
                  {isPreparingDownload
                    ? t("buttons.preparing")
                    : t("buttons.download")}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
